import WhitePlusIcon from "../../images/deliveries/whitePlusIcon.svg";
import { BackBtn } from "../../components/buttons/backBtn/BackBtn";
import { ActionBtn } from "../../components/buttons/actionBtn/ActionBtn";
import { BaseTable } from "../../components/UI/baseTable/BaseTable";
import { PageWrapper } from "../../components/UI/pageWrapper/PageWrapper";
import { fetchDrivers } from "../../redux/driver/thunks/getDriverThunk";
import { ThreeDotsIcon } from "../../images/threeDotsIcon";
import { SelectElement } from "../../components/selectElement/SelectElement";
import { fetchCustomers } from "../../redux/customer/thunks/getCustomerThunk";
import { ResetFiltersBtn } from "../../components/buttons/resetFiltersBtn/ResetFiltersBtn";
import { useTranslations } from "../../helperFunctions/customHooks/useTranslations";
import { HistoryScheduled } from "../../components/modals/historyScheduled/HistoryScheduled";
import { resetCustomersState } from "../../redux/customer/slices/getCustomerSlice";
import { MultiSelectDropdown } from "../../components/multiSelectDropdown/MultiSelectDropdown";
import { getScheduledDeliveries } from "../../redux/package/scheduledPackages/getScheduledDeliveries";
import { getCreatedScheduledById } from "../../redux/package/scheduledPackages/getCreatedScheduledById";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { resetDeleteScheduledDeliveryState } from "../../redux/package/scheduledPackages/deleteScheduledDelivery";
import {
  onEdit,
  setIsEditMode,
  setIsModalsBgShown,
  setIsWithHistory,
  setSelectedDataForEdit,
  setSelectedDriverId,
  setShouldResetScheduledPackage,
  toggleThreeDotsModal,
} from "../../redux/globalFunctions/globalFnSlice";

export function ScheduledDeliveries() {
  const dispatch = useDispatch();
  const { t } = useTranslations();
  const text = t.schedule;
  const { driver_names } = useSelector((state) => state.drivers);
  const { status, customer_names } = useSelector((state) => state.customers);
  const { status: getStatus, deliveries } = useSelector(
    (state) => state.scheduledDeliveries.getScheduledDeliveries
  );

  const { status: deleteStatus } = useSelector(
    (state) => state.scheduledDeliveries.deleteScheduledDelivery
  );

  const [filters, setFilters] = useState({
    customer_name: "",
    driver: "",
    repeat: "",
    isActive: null,
    dayOfWeek: [],
  });

  const [customerOptions, setCustomerOptions] = useState([]);
  const [driverOptions, setDriverOptions] = useState([]);
  const [packages, setPackages] = useState([]);
  const isFirstRender = useRef(true);

  useEffect(() => {
    dispatch(fetchDrivers({ names_only: true }));
    dispatch(fetchCustomers({ per_page: 1 }));
    dispatch(getScheduledDeliveries());
  }, []);

  useEffect(() => {
    const hasFilters = Object.values(filters).some((value) =>
      Array.isArray(value) ? value.length > 0 : value !== ""
    );

    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (!hasFilters) return;

    dispatch(
      getScheduledDeliveries({
        ...filters,
        repeat: filters.repeat === "ByDay" ? "Weekly" : filters.repeat,
      })
    );
    setFilters((prev) => ({ ...prev, isActive: null }));
  }, [
    filters.dayOfWeek,
    filters.driver,
    filters.repeat,
    filters.customer_name,
    dispatch,
  ]);

  function handleFilterChange(e) {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  }

  function handleDayChange(selectedDays) {
    setFilters((prev) => ({
      ...prev,
      dayOfWeek: selectedDays,
    }));
  }

  useEffect(() => {
    if (getStatus === "success") {
      setPackages(deliveries);
    }
  }, [getStatus]);

  useEffect(() => {
    if (deleteStatus === "success") {
      dispatch(resetDeleteScheduledDeliveryState());
    }
  }, [deleteStatus]);

  useEffect(() => {
    if (status === "succeeded" && customer_names.length !== 0) {
      const uniqueCustomers = Array.from(
        new Set(customer_names.map((customer) => customer.c_name))
      );
      setCustomerOptions(uniqueCustomers);
    }
  }, [status]);

  const backButton = [<BackBtn to={"/deliveries"} />];

  const columns = [
    { label: "ID" },
    { label: text.customer },
    { label: text.pickupAddress },
    { label: text.dropoffAddress },
    { label: text.repeat },
    { label: text.active },
    { label: text.history },
    { label: text.action },
  ];

  function handleResetFilters() {
    setFilters({
      customer_name: "",
      date: "",
    });
    dispatch(getScheduledDeliveries());
  }

  useEffect(() => {
    if (driver_names) {
      const uniqueDrivers = Array.from(
        new Set(driver_names.map((driver) => driver))
      );
      setDriverOptions(uniqueDrivers);
    }
  }, [driver_names]);

  const areFiltersActive = (filters) => {
    return Object.values(filters).some((value) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      if (typeof value === "boolean") {
        return true;
      }
      if (value === null || value === undefined || value === "") {
        return false;
      }
      return true;
    });
  };

  function handleThreeDotsClick(e, id, data) {
    const buttonPosition = e.currentTarget.getBoundingClientRect();
    const scrollY = window.scrollY || window.pageYOffset;

    const payload = {
      x: buttonPosition.left + window.scrollX - 35,
      y: buttonPosition.top + scrollY - 60,
    };
    dispatch(toggleThreeDotsModal(payload));
    dispatch(setIsEditMode(true));
    dispatch(setIsWithHistory(false));
    dispatch(setSelectedDriverId(id));
    dispatch(setSelectedDataForEdit(data));
  }

  function handleAddPackageClick() {
    dispatch(setShouldResetScheduledPackage(true));
    dispatch(resetCustomersState());
    dispatch(onEdit());
    dispatch(setIsEditMode(false));
  }

  const buttons = [
    <ActionBtn
      onClick={handleAddPackageClick}
      text={text.scheduleDelivery}
      imgSrc={WhitePlusIcon}
      height={"42px"}
      width={"200px"}
    />,
  ];

  const repeatOptions = [
    {
      label: t.repeatDelivery.daily,
      value: "Daily",
    },
    {
      label: t.repeatDelivery.weekly,
      value: "Weekly",
    },
    {
      label: t.repeatDelivery.monthly,
      value: "Monthly",
    },
    {
      label: t.repeatDelivery.byday,
      value: "ByDay",
    },
  ];

  const activeOptions = [
    {
      label: text.active,
      value: 1,
    },
    {
      label: text.inactive,
      value: 0,
    },
  ];

  useEffect(() => {
    if (filters.isActive !== undefined && filters.isActive !== null) {
      setPackages(
        deliveries.filter((item) => item.ds_active === filters.isActive)
      );
    }
  }, [filters.isActive]);

  const [isModalOpen, setIsModalOpen] = useState(false);

  function handleHistoryClick(boolean, id) {
    dispatch(getCreatedScheduledById({ id: id }));
    dispatch(setIsModalsBgShown(boolean));
    setIsModalOpen(boolean);
  }

  return (
    <PageWrapper backButton={backButton} title={text.title} buttons={buttons}>
      <BaseTable
        resultsContainerHeight={"calc(100vh - 300px)"}
        status={getStatus}
        errorMsg={text.errorFetchingData}
        noItemsMsg={text.noDeliveriesFound}
        items={packages}
        columns={columns}
        columnAndRowWidth={"200px"}
        filters={
          <>
            {text.filterBy}
            <SelectElement
              name="customer_name"
              id="customerSelect"
              value={filters.customer_name}
              onChange={handleFilterChange}
              placeholder={text.customerSelect}
              options={customerOptions}
              width={"250px"}
            />
            <SelectElement
              name="driver"
              id="drivers"
              value={filters.driver}
              onChange={handleFilterChange}
              placeholder={text.driversSelect}
              options={driverOptions}
              optionValueKey={"d_uid"}
              optionLabelKey={"d_name"}
              width={"170px"}
            />
            <SelectElement
              name="repeat"
              id="repeat"
              value={filters.repeat}
              onChange={handleFilterChange}
              placeholder={text.repeat}
              options={repeatOptions}
              width={"170px"}
              optionLabelKey={"label"}
              optionValueKey={"value"}
            />
            {filters.repeat === "ByDay" && (
              <MultiSelectDropdown
                placeholder={text.dayOfWeek}
                options={t.repeatDelivery.days}
                selected={filters.dayOfWeek}
                onChange={handleDayChange}
                initialOpen={true}
              />
            )}
            <SelectElement
              name="isActive"
              id="isActive"
              value={filters.isActive}
              onChange={handleFilterChange}
              placeholder={text.status}
              options={activeOptions}
              width={"170px"}
              optionLabelKey={"label"}
              optionValueKey={"value"}
            />

            <ResetFiltersBtn
              text={text.resetSelectsBtn}
              onClick={handleResetFilters}
              disabled={!areFiltersActive(filters) || getStatus === "loading"}
            />
          </>
        }
        pagination={false}
        rows={
          packages &&
          packages?.map((item) => [
            item.ds_id,
            item.customer_name,
            item.pickup_address,
            item.customer_address,
            item.ds_rrule?.custom?.freq
              ? `${item.ds_rrule.custom.freq}${
                  item.ds_rrule.custom.byweekday?.length
                    ? ` | ${item.ds_rrule.custom.byweekday.join(", ")}`
                    : ""
                }`
              : "",
            item.ds_active === 1 ? text.active : text.inactive,
            <ActionBtn
              text={"History"}
              onClick={() => handleHistoryClick(true, item.ds_id)}
            />,
            <ThreeDotsIcon
              onClick={(e) => handleThreeDotsClick(e, item.ds_id, item)}
            />,
          ])
        }
      />
      {isModalOpen && (
        <HistoryScheduled onClick={() => handleHistoryClick(false)} />
      )}
    </PageWrapper>
  );
}
